"use client";

/**
 * The "who it's for" pages (founders and business owners; the creators page was retired
 * on 2026-09-27 because GrowthSync is for entrepreneurs, founders and business owners only).
 *
 * These genuinely are siblings: same question (is this for someone like me), same
 * shape of answer. So they share one component and differ in copy, mock and photograph,
 * rather than being three near-identical files that drift apart the first time one is edited.
 * The feature pages deliberately do NOT do this, because there the shape is the argument.
 */

import { SiteHero, SectionHead, FullBleedPhoto, FaqList, CtaBand, RelatedLinks } from "@/components/marketing/site-ui";
import { AudienceMock } from "@/components/marketing/site-mocks";
import { useSiteContent } from "@/components/marketing/use-site-content";

type Variant = "founders" | "owners";

const PAGE_KEY = {
  founders: "foundersPage",
  owners: "ownersPage",
} as const;

const PHOTO: Record<Variant, { src: string; srcSmall?: string; alt: string }> = {
  founders: {
    src: "/images/messages-conversation.webp",
    alt: "Two founders talking intently across a table in the evening",
  },
  owners: {
    src: "/images/events-daylight.webp",
    srcSmall: "/images/events-daylight-1400.webp",
    alt: "Business owners arriving at a daytime event",
  },
};

const RELATED: Record<Variant, { href: string; titleKey: "discover" | "events" | "communities" | "opportunities" | "reputation" }[]> = {
  founders: [{ href: "/features/discover", titleKey: "discover" }, { href: "/features/opportunities", titleKey: "opportunities" }],
  owners: [{ href: "/features/opportunities", titleKey: "opportunities" }, { href: "/features/events", titleKey: "events" }],
};

export function AudienceClient({ variant }: { variant: Variant }) {
  const c = useSiteContent();
  const page = c[PAGE_KEY[variant]];
  const photo = PHOTO[variant];

  return (
    <>
      <SiteHero
        eyebrow={page.eyebrow}
        title={page.title}
        lede={page.lede}
        primaryLabel={page.primary}
        secondaryHref="/features"
        secondaryLabel={c.common.backToFeatures}
        aside={<AudienceMock variant={variant} />}
      />

      {/* The problem, stated once, at size. A page that opens straight into a feature list
          has skipped the only part the reader was checking for. */}
      <section className="border-y border-neutral-200 bg-white py-16 md:py-20">
        <div className="gs-page">
          <SectionHead eyebrow={page.problemEyebrow} title={page.problemTitle} body={page.problemBody} />
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="gs-page">
          <SectionHead title={page.usesHeading} />
          <div className="mt-10 grid gap-x-12 gap-y-9 md:grid-cols-2">
            {page.uses.map((use) => (
              <div key={use.title} className="min-w-0 border-t-2 border-neutral-900/85 pt-6">
                <h3 className="gs-title text-balance text-neutral-900">{use.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-neutral-600">{use.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FullBleedPhoto
        src={photo.src}
        srcSmall={photo.srcSmall}
        alt={photo.alt}
        eyebrow={page.photoEyebrow}
        title={page.photoTitle}
        body={page.photoBody}
      />

      <section className="py-16 md:py-20">
        <div className="gs-page">
          {/* The measure goes on an inner box: .gs-page centres, so a narrower max-width on it
              would recentre this section and move the page's left edge. */}
          <div className="max-w-4xl">
            <FaqList heading={c.common.faqHeading} items={page.faq} />
          </div>
        </div>
      </section>

      <RelatedLinks
        links={[
          ...RELATED[variant].map((related) => ({
            href: related.href,
            title: c.features[related.titleKey].name,
            body: c.features[related.titleKey].tagline,
          })),
          { href: "/pricing", title: c.nav.pricing, body: c.pricingPage.short },
        ]}
      />

      <div className="pt-14 md:pt-20">
        <CtaBand />
      </div>
    </>
  );
}
